<script setup lang="ts">
import { UIIcon } from '@groover-dev/groover-ui'
import { storeToRefs } from 'pinia'

import {
  Sm400Black,
  Sm400Gray,
  Sm400Pink,
  Sm600Error,
  Xs600Error,
} from '~/components/typography'

import { useDraftStore } from '~/stores/draft'

type Props = {
  typographicComponent?:
    | 'Sm400Black'
    | 'Sm400Gray'
    | 'Sm400Pink'
    | 'Sm600Error'
    | 'Xs600Error'
  iconClass: string
  text: string
  isTwoLines?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isTwoLines: false,
  typographicComponent: 'Sm400Black',
})

const { id: DRAFT_ID } = storeToRefs(useDraftStore())

const showCriteriaSocialProof = computed<boolean>(() => DRAFT_ID.value !== 0)
const component = computed(() => {
  switch (props.typographicComponent) {
    case 'Sm400Gray':
      return markRaw(Sm400Gray)
    case 'Sm400Pink':
      return markRaw(Sm400Pink)
    case 'Sm600Error':
      return markRaw(Sm600Error)
    case 'Xs600Error':
      return markRaw(Xs600Error)
    case 'Sm400Black':
    default:
      return markRaw(Sm400Black)
  }
})

const iconColorClass = computed(() => {
  switch (props.typographicComponent) {
    case 'Sm400Pink':
      return 'pink'
    case 'Sm600Error':
      return 'error'
    default:
      return ''
  }
})
</script>

<template>
  <div class="tw-flex tw-items-center">
    <UIIcon :name="iconClass" class="icon" size="sm" :class="iconColorClass" />
    <component
      :is="component"
      :class="[
        showCriteriaSocialProof && isTwoLines
          ? 'two-lines-ellipsis'
          : 'ellipsis',
      ]"
      :title="text"
    >
      {{ text }}
    </component>
  </div>
</template>

<style scoped lang="scss">
.icon {
  @apply tw-mr-sm tw-h-lg tw-w-lg tw-text-center tw-text-base tw-text-discrete2;
  &.pink {
    @apply tw-text-pink-500;
  }
  &.error {
    @apply tw-flex tw-items-center tw-justify-center tw-text-sm tw-text-error;
  }
  &.small {
    @apply tw-h-2 tw-w-2 tw-text-xs;
  }
  &.text-xs {
    @apply tw-text-xs;
  }
}
.two-lines-ellipsis {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
</style>
